<template>
    <Head title="WorkIQ Saved Snapshots" />
    <div class="flex min-h-screen bg-valence-grey-50">
        <aside class="w-60 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col px-3 py-4">
            <button
                type="button"
                aria-label="Back to playground"
                class="self-start flex items-center gap-1 rounded-full bg-white hover:bg-gray-50 px-3 py-1.5 shadow-sm border border-gray-200 text-valence-grey-750"
                data-testid="workiq-snapshots-back-button"
                @click="router.visit(workiq_playground_url)">
                <i class="bi bi-chevron-left icon-bold" aria-hidden="true"></i>
                <span class="body-small">Playground</span>
            </button>

            <div class="flex items-center gap-2 px-3 pt-5 pb-3">
                <i class="bi bi-archive text-[#0078D4]" aria-hidden="true"></i>
                <span class="body-bold text-valence-grey-900">Saved snapshots</span>
            </div>

            <p class="px-3 body-small text-valence-grey-600">Inspect the WorkIQ facts available to chat POCs, or delete a snapshot before regenerating it in the playground.</p>
        </aside>

        <main class="flex-1 overflow-y-auto">
            <div class="mx-auto max-w-5xl flex flex-col gap-5 px-5 md:px-16 py-10">
                <div class="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div class="min-w-0">
                        <BasePageTitle title="Saved WorkIQ snapshots" />
                        <p class="body-small text-valence-grey-750 mt-1">
                            Only snapshots belonging to your user and organization are shown. Deleting one does not call WorkIQ or start a new run.
                        </p>
                    </div>
                    <div class="flex items-center gap-2">
                        <BaseButton theme="secondary" size="sm" data-testid="workiq-seed-history-button" :disabled="seeding || loading" @click="seedHistory">
                            {{ seeding ? "Seeding…" : "Seed demo history" }}
                        </BaseButton>
                        <BaseButton theme="tertiary" size="sm" :disabled="loading" @click="loadSnapshots">
                            {{ loading ? "Loading…" : "Refresh" }}
                        </BaseButton>
                    </div>
                </div>

                <BaseNotification v-if="error" theme="error" :text="error" @dismiss="error = null" />
                <BaseNotification v-if="notice" theme="success" :text="notice" @dismiss="notice = null" />

                <div class="flex flex-col gap-3 rounded-xl border-2 border-[#E8E8E8] bg-white p-4 sm:flex-row sm:items-end sm:justify-between">
                    <label for="workiq-snapshot-pillar-filter" class="flex min-w-0 flex-col gap-1">
                        <span class="body-small font-semibold text-valence-grey-900">Pillar</span>
                        <select
                            id="workiq-snapshot-pillar-filter"
                            v-model="pillarFilter"
                            data-testid="workiq-snapshot-pillar-filter"
                            class="min-w-56 rounded-lg border-2 border-[#E8E8E8] bg-white px-3 py-2 body-small focus:border-valence-blue focus:outline-none">
                            <option value="">All pillars</option>
                            <option v-for="pillar in availablePillars" :key="pillar" :value="pillar">{{ pillarLabel(pillar) }}</option>
                        </select>
                    </label>
                    <div class="body-small text-valence-grey-600">{{ filteredSnapshots.length }} of {{ snapshots.length }} snapshot{{ snapshots.length === 1 ? "" : "s" }}</div>
                </div>

                <div v-if="loading && !loaded" class="body-small text-valence-grey-500">Loading saved snapshots…</div>
                <div
                    v-else-if="loaded && !snapshots.length"
                    data-testid="workiq-snapshots-empty"
                    class="rounded-xl border-2 border-dashed border-[#D8D8D8] bg-white p-8 text-center">
                    <div class="body-bold text-valence-grey-900">No saved snapshots</div>
                    <p class="body-small text-valence-grey-600 mt-1">Run a pillar ask in the WorkIQ playground to create one.</p>
                </div>
                <div v-else-if="loaded && !filteredSnapshots.length" class="body-small text-valence-grey-500">No snapshots match this pillar.</div>

                <article
                    v-for="snapshot in filteredSnapshots"
                    :key="snapshot.id"
                    :data-testid="`workiq-snapshot-${snapshot.id}`"
                    class="rounded-xl border-2 border-[#E8E8E8] bg-white p-5 flex flex-col gap-4">
                    <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div class="min-w-0 flex flex-col gap-1">
                            <div class="flex flex-wrap items-center gap-2">
                                <span class="body-bold text-valence-grey-900">{{ pillarLabel(snapshot.pillar) }}</span>
                                <span
                                    class="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                                    :class="snapshot.is_current ? 'bg-green-100 text-green-700' : 'bg-valence-grey-100 text-valence-grey-600'">
                                    {{ snapshot.is_current ? "Current" : "Expired" }}
                                </span>
                                <span v-if="snapshot.distilled?.length" class="rounded bg-[#F0F4FF] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#0078D4]">
                                    {{ snapshot.distilled.length }} distilled
                                </span>
                            </div>
                            <div class="body-small text-valence-grey-750 break-words">{{ scopeSummary(snapshot) }}</div>
                            <div class="text-xs text-valence-grey-500 break-all">{{ snapshot.scope_key }}</div>
                        </div>
                        <BaseButton
                            theme="danger"
                            size="sm"
                            :data-testid="`workiq-snapshot-delete-${snapshot.id}`"
                            :disabled="deletingId === snapshot.id"
                            @click="deleteSnapshot(snapshot)">
                            {{ deletingId === snapshot.id ? "Deleting…" : "Delete" }}
                        </BaseButton>
                    </div>

                    <dl class="grid grid-cols-1 gap-2 body-small sm:grid-cols-3">
                        <div>
                            <dt class="text-valence-grey-500">Prepared period</dt>
                            <dd class="font-medium text-valence-grey-900">{{ snapshot.prepared_period }}</dd>
                        </div>
                        <div>
                            <dt class="text-valence-grey-500">Updated</dt>
                            <dd class="font-medium text-valence-grey-900">{{ formatDate(snapshot.updated_at) }}</dd>
                        </div>
                        <div>
                            <dt class="text-valence-grey-500">Expires</dt>
                            <dd class="font-medium text-valence-grey-900">{{ formatDate(snapshot.expires_at) }}</dd>
                        </div>
                    </dl>

                    <details class="text-valence-grey-900">
                        <summary class="body-small cursor-pointer font-semibold text-valence-grey-700">Factual summary</summary>
                        <MarkdownText :text="snapshot.summary" class="prose prose-sm max-w-none mt-2 rounded-lg bg-valence-grey-50 p-3" />
                    </details>

                    <details v-if="snapshot.distilled?.length" class="text-valence-grey-900">
                        <summary class="body-small cursor-pointer font-semibold text-valence-grey-700">Optional distilled output</summary>
                        <pre class="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-valence-grey-50 p-3 text-xs">{{ pretty(snapshot.distilled) }}</pre>
                    </details>

                    <details class="text-valence-grey-900">
                        <summary class="body-small cursor-pointer font-semibold text-valence-grey-700">Scope metadata</summary>
                        <pre class="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-valence-grey-50 p-3 text-xs">{{ pretty(snapshot.scope_metadata) }}</pre>
                    </details>
                </article>
            </div>
        </main>
    </div>
</template>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { computed, onMounted, ref } from "vue";

interface Snapshot {
    id: string;
    pillar: string;
    scope_key: string;
    prepared_period: string;
    scope_metadata: Record<string, unknown>;
    summary: string;
    distilled: Record<string, unknown>[] | null;
    expires_at: string | null;
    is_current: boolean;
    created_at: string;
    updated_at: string;
}

const props = withDefaults(
    defineProps<{
        workiq_playground_url?: string;
        workiq_snapshots_api_url?: string;
        workiq_seed_history_api_url?: string;
    }>(),
    {
        workiq_playground_url: "/admin/workiq/playground/",
        workiq_snapshots_api_url: "/workiq/api/snapshots/",
        workiq_seed_history_api_url: "/workiq/api/snapshots/seed-history/",
    },
);

const SEED_WEEKS = 6;

const PILLAR_LABELS: Record<string, string> = {
    my_work: "My Work",
    journeys: "Journeys",
    collaboration_profiles: "Collaboration Profiles",
    performance_management: "Performance Review Evidence",
    collaboration_hub: "Team Dynamics",
};

const snapshots = ref<Snapshot[]>([]);
const loading = ref(false);
const loaded = ref(false);
const deletingId = ref<string | null>(null);
const seeding = ref(false);
const error = ref<string | null>(null);
const notice = ref<string | null>(null);
const pillarFilter = ref("");

const availablePillars = computed(() => [...new Set(snapshots.value.map((snapshot) => snapshot.pillar))].sort());
const filteredSnapshots = computed(() => snapshots.value.filter((snapshot) => !pillarFilter.value || snapshot.pillar === pillarFilter.value));

function pillarLabel(pillar: string): string {
    return PILLAR_LABELS[pillar] || pillar;
}

function formatDate(iso: string | null): string {
    return iso ? new Date(iso).toLocaleString() : "—";
}

function pretty(value: unknown): string {
    return JSON.stringify(value, null, 2);
}

function scopeSummary(snapshot: Snapshot): string {
    const parts = Object.entries(snapshot.scope_metadata || {})
        .map(([key, value]) => {
            const rendered = Array.isArray(value) ? value.join(", ") : String(value ?? "");
            return rendered ? `${key}: ${rendered}` : "";
        })
        .filter(Boolean);
    return parts.length ? parts.join(" · ") : snapshot.scope_key;
}

async function loadSnapshots() {
    if (loading.value) {
        return;
    }
    loading.value = true;
    error.value = null;
    try {
        const { data, response } = await useFetch(props.workiq_snapshots_api_url).get().json<{ snapshots?: Snapshot[]; error?: string }>();
        if (response.value?.ok) {
            snapshots.value = data.value?.snapshots || [];
        } else {
            error.value = data.value?.error || "Failed to load saved snapshots.";
        }
    } finally {
        loading.value = false;
        loaded.value = true;
    }
}

async function deleteSnapshot(snapshot: Snapshot) {
    if (deletingId.value || !globalThis.confirm(`Delete the ${pillarLabel(snapshot.pillar)} snapshot for ${scopeSummary(snapshot)}?`)) {
        return;
    }

    deletingId.value = snapshot.id;
    error.value = null;
    notice.value = null;
    try {
        const deleteUrl = `${props.workiq_snapshots_api_url}${snapshot.id}/`;
        const { data, response } = await useFetch(deleteUrl).delete().json<{ deleted?: string; error?: string }>();
        if (response.value?.ok && data.value?.deleted === snapshot.id) {
            snapshots.value = snapshots.value.filter((item) => item.id !== snapshot.id);
            notice.value = `${pillarLabel(snapshot.pillar)} snapshot deleted. You can regenerate it from the Pipeline page.`;
        } else {
            error.value = data.value?.error || "Failed to delete the snapshot.";
        }
    } finally {
        deletingId.value = null;
    }
}

async function seedHistory() {
    if (seeding.value || !globalThis.confirm(`Seed ${SEED_WEEKS} weeks of demo My Work snapshot history for your user? This overwrites your recent my_work/self snapshots.`)) {
        return;
    }

    seeding.value = true;
    error.value = null;
    notice.value = null;
    try {
        const { data, response } = await useFetch(props.workiq_seed_history_api_url).post({ weeks: SEED_WEEKS }).json<{ seeded?: number; created?: number; error?: string }>();
        if (response.value?.ok) {
            notice.value = `Seeded ${data.value?.seeded ?? SEED_WEEKS} weeks of My Work history. Open a My Work trajectory to see the trend.`;
            await loadSnapshots();
        } else {
            error.value = data.value?.error || "Failed to seed snapshot history.";
        }
    } finally {
        seeding.value = false;
    }
}

onMounted(loadSnapshots);
</script>
